// Sitemap generation utilities for SEO

export interface SitemapUrl {
  loc: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: number;
}

/**
 * Generate XML sitemap content
 */
export const generateSitemap = (urls: SitemapUrl[]): string => {
  const baseUrl = process.env.REACT_APP_SITE_URL || 'https://begardgear.com';
  
  const urlElements = urls.map(url => {
    const fullUrl = url.loc.startsWith('http') ? url.loc : `${baseUrl}${url.loc}`;
    
    return `  <url>
    <loc>${escapeXml(fullUrl)}</loc>${url.lastmod ? `
    <lastmod>${url.lastmod}</lastmod>` : ''}${url.changefreq ? `
    <changefreq>${url.changefreq}</changefreq>` : ''}${url.priority !== undefined ? `
    <priority>${url.priority}</priority>` : ''}
  </url>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlElements}
</urlset>`;
};

/**
 * Generate static page URLs for sitemap
 */
export const getStaticPages = (): SitemapUrl[] => {
  const now = new Date().toISOString();
  
  return [
    {
      loc: '/',
      lastmod: now,
      changefreq: 'daily',
      priority: 1.0
    },
    {
      loc: '/products',
      lastmod: now,
      changefreq: 'daily',
      priority: 0.9
    },
    {
      loc: '/about',
      lastmod: now,
      changefreq: 'monthly',
      priority: 0.7
    },
    {
      loc: '/contact',
      lastmod: now,
      changefreq: 'monthly',
      priority: 0.6
    },
    {
      loc: '/clothing-guide',
      lastmod: now,
      changefreq: 'monthly',
      priority: 0.5
    },
    {
      loc: '/disclaimer',
      lastmod: now,
      changefreq: 'yearly',
      priority: 0.3
    }
  ];
};

/**
 * Generate product URLs for sitemap from API data
 */
export const generateProductUrls = async (): Promise<SitemapUrl[]> => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    const response = await fetch(`${apiUrl}/products?limit=1000`);
    
    if (!response.ok) {
      console.warn('Failed to fetch products for sitemap');
      return [];
    }
    
    const data = await response.json();
    const products = data.products || [];
    
    return products
      .filter((product: any) => product.for_sale !== false)
      .map((product: any) => ({
        loc: `/products/${product.id}`,
        lastmod: new Date().toISOString(),
        changefreq: 'weekly' as const,
        priority: 0.8
      }));
  } catch (error) {
    console.warn('Error generating product URLs for sitemap:', error);
    return [];
  }
};

/**
 * Generate robots.txt content
 */
export const generateRobotsTxt = (): string => {
  const baseUrl = process.env.REACT_APP_SITE_URL || 'https://begardgear.com';
  
  return `User-agent: *
Allow: /

# Disallow admin and auth pages
Disallow: /admin*
Disallow: /login
Disallow: /signup
Disallow: /forgot-password
Disallow: /reset-password
Disallow: /verify-email
Disallow: /cart
Disallow: /checkout
Disallow: /profile
Disallow: /order*

# Sitemap location
Sitemap: ${baseUrl}/sitemap.xml

# Crawl delay (optional - be respectful)
Crawl-delay: 1`;
};

/**
 * Escape XML special characters
 */
const escapeXml = (str: string): string => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

/**
 * Download sitemap as file (for development/testing)
 */
export const downloadSitemap = async (): Promise<void> => {
  const staticPages = getStaticPages();
  const productPages = await generateProductUrls();
  const allPages = [...staticPages, ...productPages];
  
  const sitemapContent = generateSitemap(allPages);
  
  const blob = new Blob([sitemapContent], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = 'sitemap.xml';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Download robots.txt as file (for development/testing)
 */
export const downloadRobotsTxt = (): void => {
  const robotsContent = generateRobotsTxt();
  
  const blob = new Blob([robotsContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = 'robots.txt';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};
